import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Player, TeamInvite, TeamMember, TeamMemberRole } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Text } from '../../ui/text';
import { EmptyState, Panel, StatCard } from '../../components/PageChrome';

const ROLES: TeamMemberRole[] = ['STAFF', 'SCOREKEEPER', 'VIDEOGRAPHER', 'GUARDIAN', 'PLAYER', 'FAMILY', 'FOLLOWER'];

export default function Members() {
  const { teamId } = useParams<{ teamId: string }>();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamMemberRole>('GUARDIAN');
  const [playerId, setPlayerId] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!teamId) return;
    try {
      const [m, i, p] = await Promise.all([
        admin.listMembers(teamId),
        admin.listInvites(teamId),
        admin.listPlayers(teamId),
      ]);
      setMembers(m.members);
      setInvites(i.invites);
      setPlayers(p.players);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const counts = useMemo(() => {
    return {
      approved: members.filter((m) => m.status === 'APPROVED').length,
      pending: members.filter((m) => m.status === 'PENDING' || m.status === 'INVITED').length,
      family: members.filter((m) => m.roles.some((r) => ['GUARDIAN', 'PLAYER', 'FAMILY'].includes(r))).length,
    };
  }, [members]);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamId || !email.trim()) return;
    setBusy(true);
    setError(undefined);
    try {
      await admin.createInvite(teamId, {
        email: email.trim(),
        roles: [role],
        linkedPlayerIds: playerId ? [playerId] : undefined,
      });
      setEmail('');
      setPlayerId('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (member: TeamMember, status: TeamMember['status']) => {
    if (!teamId) return;
    try {
      await admin.setMemberStatus(teamId, member.memberId, status);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Heading level={2}>Members</Heading>
        <Text className="mt-1 text-sm">Invite staff, guardians, players, family, and followers with team-scoped access.</Text>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Approved" value={counts.approved} tone="emerald" />
        <StatCard label="Pending / invited" value={counts.pending} tone="amber" />
        <StatCard label="Linked family" value={counts.family} />
      </div>

      <Panel>
        <form onSubmit={invite} className="grid gap-4 lg:grid-cols-[1fr_12rem_1fr_auto] lg:items-end">
          <Field>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field>
            <Label>Role</Label>
            <Select value={role} onChange={(e) => setRole(e.target.value as TeamMemberRole)}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </Field>
          <Field>
            <Label>Linked player</Label>
            <Select value={playerId} onChange={(e) => setPlayerId(e.target.value)}>
              <option value="">None</option>
              {players.map((p) => <option key={p.playerId} value={p.playerId}>#{p.jerseyNumber} {p.name}</option>)}
            </Select>
          </Field>
          <Button type="submit" color="dark/zinc" disabled={busy}>{busy ? 'Inviting…' : 'Invite'}</Button>
        </form>
      </Panel>

      {error ? <Text className="text-red-400">{error}</Text> : null}

      {members.length === 0 ? (
        <EmptyState title="No members yet" body="Approvals and invites will collect here." />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {members.map((m) => (
            <li key={m.memberId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="break-all font-medium text-zinc-100">{m.displayName || m.email || m.userId || m.memberId}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge color={m.status === 'APPROVED' ? 'lime' : m.status === 'DENIED' ? 'rose' : 'amber'}>{m.status}</Badge>
                    {m.roles.map((r) => <Badge key={r} color="zinc">{r}</Badge>)}
                  </div>
                  {m.linkedPlayerIds.length ? <Text className="mt-2 text-xs">Linked players: {m.linkedPlayerIds.length}</Text> : null}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {m.status !== 'APPROVED' ? <Button plain className="text-emerald-400" onClick={() => void setStatus(m, 'APPROVED')}>Approve</Button> : null}
                  {m.status !== 'INACTIVE' ? <Button plain onClick={() => void setStatus(m, 'INACTIVE')}>Deactivate</Button> : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {invites.length ? (
        <section className="space-y-2">
          <Heading level={3}>Invites</Heading>
          <ul className="space-y-2 text-sm text-zinc-300">
            {invites.map((i) => <li key={i.inviteId} className="rounded border border-zinc-800 bg-zinc-900 p-3">{i.email} · {i.status} · {i.roles.join(', ')}</li>)}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
